import React, { useEffect, useMemo, useState } from 'react';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicket } from '@fortawesome/free-solid-svg-icons';
import ToastError from '../../../components/ToastError';
import CouponObj from '../../../features/coupons/couponObj';
import CustomerObj from '../../../features/customer/customerObj';
import { selectCustomer } from '../../../features/customer/customerSlice';
import { selectCoupons, selectCouponsLoading } from '../../../features/coupons/couponsSlice';
import CouponUtils from '../../../features/coupons/couponUtils';
import { selectCartItems, selectCartUpdateInProgress, setCartCoupon, setCartUpdateInProgress } from '../../../features/cart/cartSlice';
import { selectProducts } from '../../../features/products/productsSlice';
import CartObj from '../../../features/cart/cartObj';
import { useNavigate } from 'react-router-dom';
import { selectIsMobileRoute } from '../../../features/mobile/mobileSlice';

SwiperCore.use([Navigation]);

const YourDiscounts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [errorMsg, setErrorMsg] = useState('');
    const [errorIndex, setErrorIndex] = useState('');
    const customerData = useSelector(selectCustomer);
    const customer = useMemo(() => new CustomerObj(customerData), [customerData]);
    const couponList = useSelector(selectCoupons);
    const couponsLoading = useSelector(selectCouponsLoading);
    const products = useSelector(selectProducts);
    const updateInProgress = useSelector(selectCartUpdateInProgress);
    const cart = new CartObj(useSelector(selectCartItems));
    const AppURL = useSelector(selectIsMobileRoute);
    const urlParams = AppURL ? `/?${AppURL}` : '';
    const displayableCoupons = useMemo(() => CouponUtils.getDisplayableCoupons(couponList, customer), [couponList, customer]);

    useEffect(() => {
        const windowWidth = window.innerWidth;
        const parentElements = document.getElementsByClassName('yourdiscounts');
        if (parentElements.length > 0) {
            const parentElement = parentElements[0] as HTMLElement;
            const swiperWrapper = parentElement.querySelector('.swiper-wrapper') as HTMLElement;

            if (swiperWrapper) {
                if (displayableCoupons.length <= 4 && windowWidth >= 1200) {
                    swiperWrapper.style.display = 'flex';
                    swiperWrapper.style.justifyContent = 'center';
                } else if (displayableCoupons.length === 2 && windowWidth >= 700) {
                    swiperWrapper.style.display = 'flex';
                    swiperWrapper.style.justifyContent = 'center';
                } else if (displayableCoupons.length === 1 && windowWidth >= 325) {
                    swiperWrapper.style.display = 'flex';
                    swiperWrapper.style.justifyContent = 'center';
                } else {
                    swiperWrapper.style.removeProperty('display');
                    swiperWrapper.style.removeProperty('justify-content');
                }
            }
        }
    }, [displayableCoupons])

    const handleClick = async (coupon: any, index: number) => {
        dispatch(setCartUpdateInProgress(true));
        setErrorIndex(String(index));
        if (!updateInProgress) {
            let validationResult = await coupon.validateAgainstCart(cart, products);
            if (validationResult.error) {
                setErrorMsg(validationResult.message);
                dispatch(setCartUpdateInProgress(false));
                return;
            }
            await dispatch(setCartCoupon({ token: '', coupon: coupon.data }));
            navigate(`/checkout${urlParams}`);
        }
        dispatch(setCartUpdateInProgress(false));
    };

    return (
        <div className='pt-3 ps-2'>
            {(displayableCoupons.length > 0 || couponsLoading) && (
                <div className='fw-500 fs-32px font-poppins px-2 text-center text-uppercase'>YOUR DISCOUNTS</div>
            )}
            <div className="container0">
                <div className="detail-wrapper">
                    <div className="releted-work">
                        <Row className='coupon-list position-relative upsellsCarousel pt-0'>
                            <div className="slider-part">
                                {couponsLoading ? (
                                    <Swiper slidesPerView={4} spaceBetween={0}>
                                        {[...Array(4)].map((_, index) => (
                                            <SwiperSlide key={index} className='h-auto'>
                                                <div className='w-100 p-2 h-100'>
                                                    <Col className="coupon my-1 h-100">
                                                        <Card body className='h-100 skeleton-loader'>
                                                            <Row className='h-100'>
                                                                <Col xs={2} className='d-flex me-1'>
                                                                    <div className='skeletonDiscount skeleton-icon'></div>
                                                                </Col>
                                                                <Col xs={9} className='d-flex flex-column justify-content-center'>
                                                                    <div className='skeletonDiscount skeleton-title'></div>
                                                                    <div className='skeletonDiscount skeleton-text'></div>
                                                                    <div className='skeletonDiscount skeleton-text'></div>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    </Col>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                ) : displayableCoupons.length > 0 && (
                                    <Swiper
                                        key={displayableCoupons.length}
                                        slidesPerView={2}
                                        spaceBetween={0}
                                        className='yourdiscounts h-75'
                                        navigation={{ nextEl: ".swiper-next", prevEl: ".swiper-prev" }}
                                        scrollbar={{ draggable: true }}
                                        breakpoints={{
                                            740: { slidesPerView: 3 },
                                            1200: { slidesPerView: 4 },
                                            1600: { slidesPerView: 4 },
                                        }}>
                                        {displayableCoupons.map((couponData, index) => {
                                            let coupon = new CouponObj(couponData);
                                            return (
                                                <SwiperSlide key={index} className='h-auto'>
                                                    <div className='w-100 p-2 h-100'>
                                                        <Col className="coupon my-1 h-100">
                                                            {errorMsg && Number(errorIndex) === index && (
                                                                <ToastError
                                                                    msg={errorMsg}
                                                                    onClose={() => setErrorMsg('')}
                                                                    position="top-center"
                                                                />
                                                            )}
                                                            <Card
                                                                body
                                                                role="button"
                                                                onClick={() => handleClick(coupon, index)}
                                                                className={`${updateInProgress ? 'disabled-card' : ''} h-100 rounded-1`}
                                                            >
                                                                <Row className='h-100'>
                                                                    <Col xs={3} md={2} className='d-flex me-1'>
                                                                        <FontAwesomeIcon
                                                                            icon={faTicket}
                                                                            className="align-self-center fa-xl"
                                                                        />
                                                                    </Col>
                                                                    <Col xs={9} md={9} className='d-flex flex-column justify-content-center'>
                                                                        <div className="fw-bold">{coupon.getTitle()}</div>
                                                                        <div>{coupon.data.code}</div>
                                                                        <div>{coupon.getExpiryDate()}</div>
                                                                    </Col>
                                                                </Row>
                                                            </Card>
                                                        </Col>
                                                    </div>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                )}
                                {displayableCoupons.length > 0 && (
                                    <div className="dealpagearrow arrow-wrap">
                                        <div className="arrow-left arrow-both swiper-prev"></div>
                                        <div className="arrow-right arrow-both swiper-next"></div>
                                    </div>
                                )}
                            </div>
                        </Row>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default YourDiscounts;